// @ts-expect-error untyped
import { toContactSearch } from 'customer-data-filters/converters/contactSearch/FilterContactSearch';
import { IN_RANGE_ROLLING, TIME_UNIT_TO_DATE } from 'customer-data-filters/converters/contactSearch/FilterContactSearchOperatorTypes';
import And from 'customer-data-filters/filterQueryFormat/logic/And';
import { addCondition } from 'customer-data-filters/filterQueryFormat/logic/LogicGroup';
// @ts-expect-error untyped
import InRollingDateRange from 'customer-data-filters/filterQueryFormat/operator/InRollingDateRange';
import RollingDateConfig from 'customer-data-filters/filterQueryFormat/rollingDates/RollingDateConfig';
import { BACKWARD, FORWARD } from 'customer-data-filters/filterQueryFormat/rollingDates/RollingDateDirections';
import * as RollingDateOptions from 'customer-data-filters/filterQueryFormat/rollingDates/RollingDateOptions';
import { DUE_DATE } from 'customer-data-objects/task/TaskPropertyNames';
import I18n from 'I18n';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { Seq } from 'immutable';
import { useCallback, useMemo } from 'react';
import memoize from 'transmute/memoize';
import { syntheticEvent } from 'foundations-components/transitional/utils/SyntheticEvent';
import UISelect from 'UIComponents/input/UISelect';
import { useDispatch } from '../../context/Redux';
import { useTempFilters } from '../../hooks/useTempFilters';
import { updateTempViewCrmSearchQuery } from '../../redux/actions/tempViewState';
import { getDueDateFilter } from '../../utils/crmSearchFilters';
import { getPropertyFilter } from '../../utils/getPropertyFilterValue';
import QuickFilterContainer from './QuickFilterContainer';
import { useUserGates } from '../../hooks/auth';
import { jsx as _jsx } from "react/jsx-runtime";
const checkIfOperatorSupported = (operatorType, rollingOffset) => {
  if (rollingOffset) {
    return false;
  }
  return operatorType === IN_RANGE_ROLLING || operatorType === TIME_UNIT_TO_DATE;
};
const createRollingDateOptions = memoize(() => Seq(RollingDateOptions).map(option => ({
  help: I18n.text(option.helpToken),
  text: I18n.text(option.textToken),
  value: option.value
})).toArray());
export const getDueDateValueFromFilters = filterObject => {
  const {
    operator,
    rollForward,
    rollingOffset,
    inclusive,
    timeUnit,
    timeUnitCount
  } = filterObject || {};
  if (!filterObject ||
  // If the operator type is not supported in the quick filter then we don't
  // want to use the quick filter, it will show up in the advanced panel
  !checkIfOperatorSupported(operator, rollingOffset)) {
    return '';
  } else {
    const direction = rollForward ? FORWARD : BACKWARD;
    const includeFutureDates = operator === IN_RANGE_ROLLING;
    const rollingDateConfig = RollingDateConfig({
      direction,
      includeFutureDates,
      isInclusive: Boolean(inclusive),
      timeUnit,
      value: timeUnitCount
    });
    return RollingDateConfig.toRollingDateOptionValue(rollingDateConfig);
  }
};
const DueDateFilter = ({
  viewId
}) => {
  const options = createRollingDateOptions();
  const dispatch = useDispatch();
  const crmSearchFilters = useTempFilters(viewId);
  const filterObject = getPropertyFilter(crmSearchFilters, DUE_DATE);
  const value = useMemo(() => getDueDateValueFromFilters(filterObject), [filterObject]);
  const userGates = useUserGates();
  const getIsUngated = useCallback(gate => userGates.includes(gate), [userGates]);
  const handleChange = useCallback(
  // TODO Change this type to be RollingDateOptionValuesTypes instead of string. Will likely require some logic adjustments
  // to handle '' as a possible value here, as that is not a RollingDateOptionValuesTypes
  e => {
    const eventValue = e.target.value;
    // the select options have a preset as the value (ex: TODAY), so we need to
    // convert them into the crmSearch expected format
    const newRollingDateConfig = RollingDateConfig.fromRollingDateOptionValue(eventValue);
    const filterQueryFormat = newRollingDateConfig ? InRollingDateRange.of(DUE_DATE, newRollingDateConfig) : null;
    const filtersWithoutDueDate = crmSearchFilters.filter(filter => filter.property !== DUE_DATE);
    let newFilters;
    if (!eventValue || !filterQueryFormat) {
      newFilters = filtersWithoutDueDate;
    } else {
      const dueDateFilter = toContactSearch(addCondition(filterQueryFormat, And()), getIsUngated);
      newFilters = [...filtersWithoutDueDate, getDueDateFilter(dueDateFilter[0])];
    }
    dispatch(updateTempViewCrmSearchQuery({
      viewId,
      crmSearchFilters: newFilters
    }));
  }, [crmSearchFilters, dispatch, getIsUngated, viewId]);
  const renderButtonContent = useCallback(() => {
    return /*#__PURE__*/_jsx(FormattedMessage, {
      message: 'tasks-table.filters.dueDate.label'
    });
  }, []);
  const shouldHighlightFilter = Boolean(filterObject);
  return /*#__PURE__*/_jsx(QuickFilterContainer, {
    filterName: "due-date",
    shouldHighlight: shouldHighlightFilter,
    shouldShowClearButton: shouldHighlightFilter,
    onClear: () => handleChange(syntheticEvent('')),
    children: /*#__PURE__*/_jsx(UISelect, {
      ButtonContent: renderButtonContent,
      buttonUse: "transparent",
      "data-selenium-test": "due-date-quick-filter",
      "data-test-id": "due-date-quick-filter",
      menuWidth: "auto",
      onChange: handleChange,
      options: options,
      value: value
    })
  });
};
export default DueDateFilter;